import React from "react";
// Dashboard 360 Map Search Bar

// Default
import { useState } from "react";

// Mui
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

// Svg
import DownArrow from "../../assets/svg/downArrow";
import Calendar1 from "../../assets/svg/calendar1";
import Search3 from "../../assets/svg/search3";

// Theme
import theme from "../../theme";

// Custom
import CustomSelect from "../../forms/select/CustomSelect";

// Json
import markersData from "../../json/Meter.json";
import { MarkerData } from "../../types";

// Types and Interfaces
interface OptionType {
  type: string;
  value: string;
}

interface SearchBarContentItem {
  icon: React.ElementType;
  title: string;
  options: OptionType[];
}

interface Dashboard360MapSearchBarProps {
  setIsClicked: (clicked: boolean) => void;
  setMarkers: (markers: MarkerData[]) => void;
  setStatus: (status: string) => void;
}

interface MarkersDataType {
  markers: MarkerData[];
}

const Dashboard360MapSearchBar: React.FC<Dashboard360MapSearchBarProps> = ({
  setIsClicked,
  setMarkers,
  setStatus,
}) => {
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const [meterStatus, setMeterStatus] = useState<OptionType | null>(null);
  const [customerType, setCustomerType] = useState<OptionType | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<OptionType | null>(
    null
  );
  const [recentEvent, setRecentEvent] = useState<OptionType | null>(null);
  const [anomalyDetection, setAnomalyDetection] = useState<OptionType | null>(
    null
  );

  const handleChangeMeterStatus = (status: OptionType): void => {
    setMeterStatus(status);
    setStatus(status?.value);
    const typedMarkersData = markersData as MarkersDataType;
    const filterData = typedMarkersData.markers.filter(
      (item: MarkerData) => item.status === status?.value
    );
    setMarkers(filterData);
  };

  const SEARCH_BAR_CONTENT: SearchBarContentItem[] = [
    {
      icon: DownArrow,
      title: "Meter Status",
      options: [
        { type: "Active", value: "active" },
        { type: "Inactive", value: "inactive" },
        { type: "Tampered", value: "tampered" },
      ],
    },
    {
      icon: Calendar1,
      title: "Installation Date",
      options: [],
    },
    {
      icon: DownArrow,
      title: "Consumer Type",
      options: [
        { type: "Residential", value: "Residential" },
        { type: "Commercial", value: "Commercial" },
        { type: "Industrial", value: "Industrial" },
      ],
    },
    {
      icon: Search3,
      title: "Pincode",
      options: [],
    },
    {
      icon: DownArrow,
      title: "Connection Status",
      options: [
        { type: "Connected", value: "Connected" },
        { type: "Disconnected", value: "Disconnected" },
      ],
    },
    {
      icon: DownArrow,
      title: "Recent Events",
      options: [
        { type: "Voltage Fluctuations", value: "Voltage Fluctuations" },
        { type: "Communication Failures", value: "Communication Failures" },
        { type: "Power Outages", value: "Power Outages" },
        { type: "Meter Tamper", value: "Meter Tamper" },
      ],
    },
    {
      icon: DownArrow,
      title: "Anomaly Detection",
      options: [
        { type: "Magnetic Tampering", value: "Magnetic Tampering" },
        { type: "Physical Tampering", value: "Physical Tampering" },
        { type: "Overvoltage", value: "Overvoltage" },
        { type: "Undervoltage", value: "Undervoltage" },
        { type: "Excessive Consumption", value: "Excessive Consumption" },
        { type: "Battery Low", value: "Battery Low" },
      ],
    },
  ];

  return (
    <Stack
      direction={"row"}
      gap={matches ? "12px" : "20px"}
      alignItems={"center"}
      pl={"30px"}
      pr={"15px"}
      onClick={() => setIsClicked(true)}>
      <CustomSelect
        value={meterStatus?.type || ""}
        onChange={(e) => {
          const selectedOption = SEARCH_BAR_CONTENT[0].options.find(
            (option: OptionType) => option.value === e.target.value
          );
          if (selectedOption) {
            handleChangeMeterStatus(selectedOption);
          }
        }}
        icon={SEARCH_BAR_CONTENT[0].icon}
        title={SEARCH_BAR_CONTENT[0].title}
        options={SEARCH_BAR_CONTENT[0].options}
      />

      <Stack direction={"row"} gap={"9px"} alignItems={"center"}>
        <Typography variant="body2">{SEARCH_BAR_CONTENT[1].title}</Typography>
        <Box mb={"2px"}>
          {(() => {
            const IconComponent = SEARCH_BAR_CONTENT[1].icon;
            return <IconComponent />;
          })()}
        </Box>
      </Stack>

      <CustomSelect
        value={customerType?.type || ""}
        onChange={(e) => {
          const selectedOption = SEARCH_BAR_CONTENT[2].options.find(
            (option: OptionType) => option.value === e.target.value
          );
          setCustomerType(selectedOption || null);
        }}
        icon={SEARCH_BAR_CONTENT[2].icon}
        title={SEARCH_BAR_CONTENT[2].title}
        options={SEARCH_BAR_CONTENT[2].options}
      />

      <Stack direction={"row"} gap={"9px"} alignItems={"center"}>
        <Typography variant="body2">{SEARCH_BAR_CONTENT[3].title}</Typography>
        <Box mb={"2px"}>
          {(() => {
            const IconComponent = SEARCH_BAR_CONTENT[3].icon;
            return <IconComponent />;
          })()}
        </Box>
      </Stack>
      {/* {SEARCH_BAR_CONTENT[3].icon} */}

      <CustomSelect
        value={connectionStatus?.type || ""}
        onChange={(e) => {
          const selectedOption = SEARCH_BAR_CONTENT[4].options.find(
            (option: OptionType) => option.value === e.target.value
          );
          setConnectionStatus(selectedOption || null);
        }}
        icon={SEARCH_BAR_CONTENT[4].icon}
        title={SEARCH_BAR_CONTENT[4].title}
        options={SEARCH_BAR_CONTENT[4].options}
      />

      <CustomSelect
        value={recentEvent?.type || ""}
        onChange={(e) => {
          const selectedOption = SEARCH_BAR_CONTENT[5].options.find(
            (option: OptionType) => option.value === e.target.value
          );
          setRecentEvent(selectedOption || null);
        }}
        icon={SEARCH_BAR_CONTENT[5].icon}
        title={SEARCH_BAR_CONTENT[5].title}
        options={SEARCH_BAR_CONTENT[5].options}
      />

      <CustomSelect
        value={anomalyDetection?.type || ""}
        onChange={(e) => {
          const selectedOption = SEARCH_BAR_CONTENT[6].options.find(
            (option: OptionType) => option.value === e.target.value
          );
          setAnomalyDetection(selectedOption || null);
        }}
        icon={SEARCH_BAR_CONTENT[6].icon}
        title={SEARCH_BAR_CONTENT[6].title}
        options={SEARCH_BAR_CONTENT[6].options}
      />
    </Stack>
  );
};

export default Dashboard360MapSearchBar;
